import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const CLEAR_CHROME_CLASS = 'comm-copy-history-blocked-prompt--clear-chrome';

const CONTAINER_SELECTOR = [
  '.appointment-requests-container',
  '.refill-requests-container',
  '.inquiries-container',
].join(', ');

const rectsOverlap = (a, b) =>
  !(
    a.bottom <= b.top ||
    a.top >= b.bottom ||
    a.right <= b.left ||
    a.left >= b.right
  );

const commBlockedPromptBody = (recordLabel) => (
  <>
    <strong className="comm-copy-history-blocked-prompt__lead">
      Your browser blocked automatic copying.
    </strong>
    <ol className="comm-copy-history-blocked-prompt__steps">
      <li>
        Click this <strong>{recordLabel}</strong> row to open its details.
      </li>
      <li>
        Click the <strong>Copy History</strong> button next to
        <br />
        the <strong>Message History</strong> heading.
      </li>
      <li>If you don&apos;t see the button, scroll down.</li>
    </ol>
  </>
);

const updateHistoryBlockedPromptBody = (
  <>
    <strong className="comm-copy-history-blocked-prompt__lead">
      Your browser blocked automatic copying.
    </strong>
    <ol className="comm-copy-history-blocked-prompt__steps">
      <li>
        Click <em>View history</em> for this update to open its update history.
      </li>
      <li>
        Click the <strong>Copy History</strong> button next to the{' '}
        <strong>Update History</strong> heading.
      </li>
    </ol>
  </>
);

/**
 * Clipboard-blocked Copy History prompt.
 * Comms tables: pass recordLabel (singular). Collision with .sticky-table
 * applies --clear-chrome (bottom offset via CSS).
 * Update History: variant="updateHistory" (no comm chrome shift).
 */
const CommCopyHistoryBlockedPrompt = ({ recordLabel, variant }) => {
  const ref = useRef(null);
  const [clearChrome, setClearChrome] = useState(false);
  const isUpdateHistory = variant === 'updateHistory';

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || isUpdateHistory) {
      return undefined;
    }

    const update = () => {
      // Measure at the unshifted position only (default bottom: 100%).
      el.style.bottom = '100%';

      const container = el.closest(CONTAINER_SELECTOR);
      const chrome = container?.querySelector('.sticky-table');
      let needsShift = false;

      if (chrome) {
        needsShift = rectsOverlap(
          el.getBoundingClientRect(),
          chrome.getBoundingClientRect(),
        );
      }

      el.style.bottom = '';
      setClearChrome(needsShift);
    };

    update();
    const raf = window.requestAnimationFrame(update);

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [isUpdateHistory]);

  return (
    <span
      ref={ref}
      className={
        clearChrome
          ? `comm-copy-history-blocked-prompt ${CLEAR_CHROME_CLASS}`
          : 'comm-copy-history-blocked-prompt'
      }
    >
      {isUpdateHistory
        ? updateHistoryBlockedPromptBody
        : commBlockedPromptBody(recordLabel)}
    </span>
  );
};

CommCopyHistoryBlockedPrompt.propTypes = {
  recordLabel: PropTypes.string,
  variant: PropTypes.oneOf(['updateHistory']),
};

CommCopyHistoryBlockedPrompt.defaultProps = {
  recordLabel: '',
  variant: undefined,
};

export default CommCopyHistoryBlockedPrompt;
